import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import {
    Box,
    Breadcrumbs,
    Button,
    CardHeader,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid, MenuItem, Paper,
    Radio,
    RadioGroup, Select, Switch, Tab, TextField,
    Typography
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import { withStyles } from "@mui/styles";
import axios from "axios";
import { Buffer } from "buffer";
import { SchedulerRoot } from "material-ui-cron";
import moment from "moment";
import { createForm } from "rc-form";
import * as React from "react";
import ReactCountryFlag from "react-country-flag";
import { Link } from "react-router-dom";
import { Config } from "../../config/config";
import { CountryCodes } from "../../utils/countryCodes";
import { Helpers } from "../../utils/helpers";
import { PopupMessage } from "../../utils/message";
import { EditFile } from "../Bundles/components/EditFile";
import { EventStats } from "./eventStats";

interface Props {
    activeUser: any;
    activeEnv: any;
    classes: any;
    form: any;
    match: any;
}

interface EventProps {
    enabled: boolean;
    grace_period: number;
    key: string;
    type: string;
    end_value: string;
    start_at: string;
    leaderboard_enabled: boolean;
    groups_enabled: boolean;
    group_size: number;
    groups: any[];
    has_trigger: boolean;
    trigger: null | string;
    trigger_value: null | string;
    is_traceable: boolean;
    registration_enabled: boolean;
    registration: null | number;
    restricted_countries: string[];
    group: string;
    name: string;
    description: string;
    availability: string;
    analyticsType: string;
}

interface State {
    event: EventProps;
    loaded: boolean;
    applyOnline: boolean;
    newEvent: boolean;
    dialogVisible: boolean;
    savingData: boolean;
    activeTab: any;
}

class SingleGameEventComponent extends React.Component<Props, State> {
    _editorRef;

    constructor(props) {
        super(props);

        this.state = {
            event: {
                enabled: false,
                grace_period: 0,
                key: this.props.match.params.gameEventKey,
                type: "duration",
                end_value: "1",
                start_at: "* * * * *",
                leaderboard_enabled: false,
                groups_enabled: false,
                group_size: 0,
                groups: [],
                has_trigger: false,
                trigger: null,
                trigger_value: null,
                is_traceable: false,
                registration_enabled: false,
                registration: null,
                restricted_countries: [],
                group: "custom",
                name: "",
                description: "",
                availability: "ALL THE TIME",
                analyticsType: "ONETIME",
            },
            activeTab: "configuration",
            applyOnline: false,
            newEvent: false,
            loaded: false,
            dialogVisible: false,
            savingData: false,
        };

        document.title = Helpers.documentTitle("Game Events");

        this._editorRef = React.createRef();
    }

    handleCheckboxChange = (e: any) => {
        const data = {...this.state.event}
        data[e.target.name] = e.target.checked;
        this.setState({
            event: data
        });
    }

    onGracePeriodChange = (e) => {
        const data = {...this.state.event}

        data[e.target.name] = e.target.value;

        this.setState({
            event: data
        });
    }

    onGracePEriodToggle = (e) => {
        const data = {...this.state.event}
        data[e.target.name] = e.target.checked ? 0 : null;
        this.setState({
            event: data
        });
    }

    handleTextFieldChange = (e: any) => {
        const data = {...this.state.event}

        if (e.target.type === "checkbox") {
            data[e.target.name] = e.target.checked;
        } else if (e.target.type === "number") {
            data[e.target.name] = parseInt(e.target.value, 10);
        } else {
            data[e.target.name] = e.target.value;
        }

        this.setState({
            event: data
        });
    }

    handleCronError = (error: string): void => {
        if (error) {
            PopupMessage.error(`Cron error: ${error}`);
        }
    }

    handleStartsAt = (value: string): void => {
        const data = {...this.state.event}
        data.start_at = value;
        this.setState({
            event: data
        });
    }

    handleOnlinePublish = (event) => {
        this.setState({
            applyOnline: event.target.checked,
        });
    }

    handleEndAt = (value: string): void => {
        const data = {...this.state.event}
        data.end_value = value;
        this.setState({
            event: data
        });
    }

    componentDidMount() {
        this.fetchData();
    }

    headers() {
        return {
            headers: { Authorization: Helpers.getSessionId() },
            timeout: 30000
        }
    }

    normalizeCronValue(value: string | null) {
        if (!value) {
            return "";
        }

        let parts = value.split(" ");

        if (5 !== parts.length) {
            return "* * * * *";
        }

        parts = parts.map(part => {
            if (2 === part.length) {
                // This fixes invalid cron values like "00 00 * * *", leading zero's are not needed
                if ("0" === part[0]) {
                    return part[1];
                }
            }
            return part;
        });

        return parts.join(" ");
    }

    displayDuration() {
        if ("duration" === this.state.event.type) {
            return <TextField
                label="Duration"
                name="end_value"
                type="number"
                value={this.state.event.end_value}
                onChange={this.handleTextFieldChange}
                variant="outlined"
                size="small"
                style={{ marginTop: 10 }}
            />;
        }

        return null;
    }

    displayEndAt() {
        if ("endAt" === this.state.event.type) {
            return <SchedulerRoot
                cron={this.normalizeCronValue(this.state.event.end_value)}
                setCron={this.handleEndAt}
                setCronError={this.handleCronError}
            />;
        }

        return null;
    }

    async applyChanges() {
        this.setState({ savingData: true }, async () => {
            try {
                if (Helpers.isStaging() && this._editorRef && this._editorRef.current) {
                    await this._editorRef.current.saveRawJson();
                }

                await axios.put(
                    `${Config.getInstance.apiUrl}/api/game_events`,
                    {
                        ...this.state.event,
                        applyOnline: this.state.applyOnline,
                    },
                    this.headers(),
                );

                PopupMessage.success("Event data updated!");

                this.setState({ savingData: false });
            } catch (e) {
                PopupMessage.error(e.response.data.message);
                this.setState({ savingData: false });
            }
        })
    }

    goBackToList() {
        setTimeout(() => {
            document.location.pathname = "/game_events";
        }, 1000);
    }

    deleteEvent = async () => {
        if (confirm("Are you sure? This action cannot be undone!")) {
            try {
                await axios.delete(
                    `${Config.getInstance.apiUrl}/api/game_events/${this.state.event.key}`,
                    this.headers(),
                );

                PopupMessage.success("Game event deleted");
                this.goBackToList();
            } catch (e) {
                PopupMessage.error(e.response.data.message);
            }
        }
    }

    async fetchData() {
        if (this.state.loaded) {
            return;
        }

        if ("new" === this.state.event.key) {
            const eventData = {...this.state.event};
            eventData.key = "";

            this.setState({
                event: eventData,
                loaded: true,
                newEvent: true,
            });
            return;
        }

        try {
            const response = await axios.get(`${Config.getInstance.apiUrl}/api/game_events`, this.headers());
            const event = response.data.find(record => record.key === this.state.event.key);
            if (event) {
                this.setState({
                    event: {
                        ...this.state.event,
                        ...event,
                    },
                    loaded: true,
                    newEvent: false,
                });
            } else {
                this.goBackToList();
            }
        } catch (e) {
            PopupMessage.error(e.response.data.message);
        }
    }

    handleCancel() {
        this.setState({ dialogVisible: false })
    }

    handleShow(e) {
        e.preventDefault();
        this.setState({ dialogVisible: true })
    }

    removeGroup(index) {
        const tempEvent = { ...this.state.event };

        tempEvent.groups.splice(index, 1);

        this.setState({ event: tempEvent });
    }

    handleTabChange(value) {
        this.setState({
            activeTab: value
        })
    }

    handleGroupValueChange(value, key, index) {
        const tempEvent = { ...this.state.event };

        tempEvent.groups[index][key] = parseInt(value, 10);

        this.setState({ event: tempEvent });
    }

    addCountry() {
        const tempEvent = { ...this.state.event };

        tempEvent.restricted_countries.push(null);

        this.setState({ event: tempEvent });
    }

    handleRestrictedCountryChange(index, value) {
        const tempEvent = { ...this.state.event };

        tempEvent.restricted_countries[index] = value;

        this.setState({ event: tempEvent });
    }

    removeRestrictedCountry(index) {
        const tempEvent = { ...this.state.event };

        tempEvent.restricted_countries.splice(index, 1);

        this.setState({ event: tempEvent });
    }

    addGroup() {
        const tempEvent = { ...this.state.event };

        tempEvent.groups.push({
            min: 0,
            max: 0,
        });

        this.setState({ event: tempEvent });
    }

    displayKey() {
        if (this.state.newEvent) {
            return <TextField
                name="key"
                type="text"
                value={this.state.event.key}
                onChange={this.handleTextFieldChange}
                variant="outlined"
                size="small"
            />;
        }

        return <div>{this.state.event.key}</div>;
    }

    renderScheduleInfo() {
        const {classes} = this.props;
        const startAt = moment(Helpers.parseCron(this.state.event.start_at).next().toDate());
        const endAt = this.state.event.type === "endAt" ?
            moment(Helpers.parseCron(this.state.event.end_value).next().toDate()) :
            startAt.clone().add(this.state.event.end_value, "days")

        return <Grid container spacing={3} style={{ padding: "0 16px 0 16px" }}>
            <Grid item sm={this.state.event.grace_period !== null ? 4 : 6} style={{ paddingTop: 12, paddingBottom: 12 }}>
                <div className={classes.title}>
                    <Typography style={{ textAlign: "center" }}>
                        Starts at
                    </Typography>

                    <Typography style={{ textAlign: "center" }}>
                        {startAt.format("DD.MM.YYYY HH:mm")}
                    </Typography>
                </div>
            </Grid>

            <Grid item sm={this.state.event.grace_period !== null ? 4 : 6} style={{ paddingTop: 12, paddingBottom: 12 }}>
                <div className={classes.title}>
                    <Typography style={{ textAlign: "center" }}>
                        Ends at
                    </Typography>

                    <Typography style={{ textAlign: "center" }}>
                        {endAt.format("DD.MM.YYYY HH:mm")}
                    </Typography>
                </div>
            </Grid>

            {this.state.event.grace_period !== null &&
                <Grid item sm={4} style={{ paddingTop: 12, paddingBottom: 12 }}>
                    <div className={classes.title}>
                        <Typography style={{ textAlign: "center" }}>
                            Grace period ends at
                        </Typography>

                        <Typography style={{ textAlign: "center" }}>
                            {endAt.clone().add(this.state.event.grace_period, "days").format("DD.MM.YYYY HH:mm")}
                        </Typography>
                    </div>
                </Grid>
            }
        </Grid>
    }

    render() {
        if (!this.state.loaded) {
            return <div className="contentLoading">
                <CircularProgress/>
            </div>
        }

        return (
            <TabContext value={this.state.activeTab}>
                <div className="contentPadding">
                    <CardHeader
                        title={
                            <Breadcrumbs aria-label="breadcrumb">
                                <Link to="/game_events">
                                    Events
                                </Link>
                                <Typography color="textPrimary">{this.state.event.key}</Typography>
                            </Breadcrumbs>
                        }
                        action={this.state.activeTab === "configuration" && <React.Fragment>
                            <Grid container spacing={2}>
                                <Grid item>
                                    <FormControlLabel
                                        control={<Checkbox
                                            checked={this.state.applyOnline}
                                            onChange={this.handleOnlinePublish}
                                            disabled={this.state.savingData}
                                        />}
                                        label="Apply changes for online players"
                                    />
                                </Grid>
                                {!Helpers.isProd() && <Grid item>
                                    <Button disableElevation
                                        color="secondary"
                                        size="medium"
                                        variant="contained"
                                        onClick={() => this.deleteEvent()}
                                        disabled={this.state.savingData}
                                    >
                                        Delete event
                                    </Button>
                                </Grid>}
                                <Grid item>
                                    <Button disableElevation
                                        color="primary"
                                        size="medium"
                                        variant="contained"
                                        onClick={() => this.applyChanges()}
                                        disabled={this.state.savingData}
                                    >
                                        Save
                                    </Button>
                                </Grid>
                            </Grid>
                        </React.Fragment>}
                    />

                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                        <TabList onChange={(_e, newValue) => this.handleTabChange(newValue)}>
                            <Tab label="Configuration" value="configuration"/>
                            <Tab label="Stats" value="stats"/>
                        </TabList>
                    </Box>

                    <TabPanel value="configuration" style={{ paddingLeft: 0, paddingRight: 0 }}>
                        <Grid container spacing={2} style={{ marginTop: 10 }}>
                            <Grid item sm={8}>
                                <Paper elevation={0}>
                                    {this.renderScheduleInfo()}
                                </Paper>

                                <Paper elevation={0} style={{ marginTop: 32, marginBottom: 32 }}>
                                    <Grid container spacing={1} style={{ padding: 16 }} className="single-event-edit">
                                        <Grid item sm={6}>
                                            <Typography variant="h6">Starts At</Typography>

                                            <SchedulerRoot
                                                cron={this.normalizeCronValue(this.state.event.start_at)}
                                                setCron={this.handleStartsAt}
                                                setCronError={this.handleCronError}
                                            />
                                        </Grid>
                                        <Grid item sm={6}>
                                            <Typography variant="h6">Ends At</Typography>

                                            {this.displayDuration()}
                                            {this.displayEndAt()}
                                        </Grid>
                                    </Grid>
                                </Paper>

                                {!Helpers.isProd() && <EditFile
                                    ref={this._editorRef}
                                    hideSaveButton={true}
                                    fullWidth={true}
                                    hideBreadcrumbs={true}
                                    location={{
                                        search: "?source=game"
                                    }}
                                    match={{
                                        params: {
                                            key: Buffer.from(`events/types/${this.state.event.key}.json`, "ascii").toString("base64")
                                        }
                                    }}
                                />}
                            </Grid>
                            <Grid item sm={4}>
                                <Paper elevation={0}>
                                    <Grid container spacing={3} style={{ padding: 16 }}>
                                        <Grid item sm={12}>
                                            <FormControlLabel
                                                className="control-label-full"
                                                label="Key"
                                                labelPlacement="start"
                                                control={this.displayKey()}
                                            />
                                        </Grid>
                                        <Grid item sm={12}>
                                            <FormControlLabel
                                                className="control-label-full"
                                                label="Enabled"
                                                labelPlacement="start"
                                                control={
                                                    <Switch
                                                        checked={this.state.event.enabled}
                                                        onChange={this.handleCheckboxChange}
                                                        name="enabled"
                                                        color="primary"
                                                    />
                                                }
                                            />
                                        </Grid>
                                        <Grid item sm={12}>
                                            <FormControlLabel
                                                className="control-label-full"
                                                label="End Type"
                                                labelPlacement="start"
                                                control={
                                                    <RadioGroup
                                                        row={true}
                                                        value={this.state.event.type}
                                                        onChange={this.handleTextFieldChange}
                                                        name="type"
                                                    >
                                                        <FormControlLabel value="endAt" control={<Radio color="primary"/>} label="Ends At"/>
                                                        <FormControlLabel value="duration" control={<Radio color="primary"/>} label="Duration"/>
                                                    </RadioGroup>
                                                }
                                            />
                                        </Grid>
                                        <Grid item sm={12}>
                                            <FormControlLabel
                                                className="control-label-full"
                                                label="Name"
                                                labelPlacement="start"
                                                control={<TextField
                                                    name="name"
                                                    value={this.state.event.name}
                                                    onChange={this.onGracePeriodChange}
                                                    variant="outlined"
                                                    size="small"
                                                    style={{ width: "70%" }}
                                                />}
                                            />
                                        </Grid>
                                        <Grid item sm={12}>
                                            <FormControlLabel
                                                className="control-label-full"
                                                label="Description"
                                                labelPlacement="start"
                                                control={<TextField
                                                    name="description"
                                                    value={this.state.event.description}
                                                    onChange={this.onGracePeriodChange}
                                                    variant="outlined"
                                                    size="small"
                                                    style={{ width: "70%" }}
                                                />}
                                            />
                                        </Grid>
                                        <Grid item sm={12}>
                                            <FormControlLabel
                                                className="control-label-full"
                                                label="Group"
                                                labelPlacement="start"
                                                control={
                                                    <Select
                                                        value={this.state.event.group}
                                                        onChange={this.handleTextFieldChange}
                                                        name="group"
                                                        variant="outlined"
                                                        className="small-select"
                                                    >
                                                        <MenuItem value="custom">Custom</MenuItem>
                                                        <MenuItem value="gather">Gather</MenuItem>
                                                        <MenuItem value="battle">Battle</MenuItem>
                                                    </Select>
                                                }
                                            />
                                        </Grid>
                                        <Grid item sm={12}>
                                            <FormControlLabel
                                                className="control-label-full"
                                                label="Availability"
                                                labelPlacement="start"
                                                control={
                                                    <Select
                                                        value={this.state.event.availability}
                                                        onChange={this.handleTextFieldChange}
                                                        name="availability"
                                                        variant="outlined"
                                                        className="small-select"
                                                    >
                                                        <MenuItem value="ALL THE TIME">All the time</MenuItem>
                                                        <MenuItem value="WHEN ACTIVE">When active</MenuItem>
                                                        <MenuItem value="WHEN INACTIVE">When inactive</MenuItem>
                                                    </Select>
                                                }
                                            />
                                        </Grid>
                                        <Grid item sm={12}>
                                            <FormControlLabel
                                                className="control-label-full"
                                                label="Analytics type"
                                                labelPlacement="start"
                                                control={
                                                    <Select
                                                        value={this.state.event.analyticsType}
                                                        onChange={this.handleTextFieldChange}
                                                        name="analyticsType"
                                                        variant="outlined"
                                                        className="small-select"
                                                    >
                                                        <MenuItem value="ONETIME">One time</MenuItem>
                                                        <MenuItem value="PERIODIC">Periodic</MenuItem>
                                                        <MenuItem value="DISABLED">Disabled</MenuItem>
                                                    </Select>
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Paper>

                                <Paper elevation={0} style={{ marginTop: 32 }}>
                                    <CardHeader title="Grace period" className="small-header"/>

                                    <Grid container spacing={3} style={{ padding: "0 16px 16px 16px" }}>
                                        <Grid item sm={12}>
                                            <FormControlLabel
                                                className="control-label-full"
                                                label="Enabled"
                                                labelPlacement="start"
                                                control={
                                                    <Switch
                                                        checked={this.state.event.grace_period !== null}
                                                        onChange={this.onGracePEriodToggle}
                                                        name="grace_period"
                                                        color="primary"
                                                    />
                                                }
                                            />

                                            <p style={{ color: "#838383", fontSize: 14, lineHeight: "20px" }}>
                                                Event will be visible x days after event has been ended.
                                            </p>
                                        </Grid>
                                        {this.state.event.grace_period !== null && <Grid item sm={12}>
                                            <TextField
                                                label="Grace period"
                                                type="number"
                                                inputProps={{min: 0}}
                                                name="grace_period"
                                                value={this.state.event.grace_period}
                                                onChange={this.onGracePeriodChange}
                                                variant="outlined"
                                                size="small"
                                                style={{ width: "100%" }}
                                            />
                                        </Grid>}
                                    </Grid>
                                </Paper>

                                <Paper elevation={0} style={{ marginTop: 32 }}>
                                    <CardHeader title="Leaderboard" className="small-header"/>

                                    <Grid container spacing={3} style={{ padding: "0 16px 16px 16px" }}>
                                        <Grid item sm={12}>
                                            <FormControlLabel
                                                className="control-label-full"
                                                label="Enabled"
                                                labelPlacement="start"
                                                control={
                                                    <Switch
                                                        name="leaderboard_enabled"
                                                        checked={this.state.event.leaderboard_enabled}
                                                        onChange={this.handleTextFieldChange}
                                                        color="primary"
                                                    />
                                                }
                                            />

                                            <p style={{ color: "#838383", fontSize: 14, lineHeight: "20px" }}>
                                                If enabled then user progress will be tracked for the leaderboard.
                                            </p>
                                        </Grid>

                                        {this.state.event.leaderboard_enabled && <React.Fragment>
                                            <Grid item sm={12}>
                                                <FormControlLabel
                                                    className="control-label-full"
                                                    label="Groups enabled"
                                                    labelPlacement="start"
                                                    control={
                                                        <Switch
                                                            name="groups_enabled"
                                                            checked={this.state.event.groups_enabled}
                                                            onChange={this.handleTextFieldChange}
                                                            color="primary"
                                                        />
                                                    }
                                                />

                                                <p style={{ color: "#838383", fontSize: 14, lineHeight: "20px" }}>
                                                    Group leaderboards by max amount of users in the group.
                                                </p>
                                            </Grid>

                                            {this.state.event.groups_enabled && <Grid item sm={12}>
                                                <TextField
                                                    label="Group size"
                                                    type="number"
                                                    inputProps={{ min: 0 }}
                                                    name="group_size"
                                                    variant="outlined"
                                                    size="small"
                                                    style={{ width: "100%" }}
                                                    value={this.state.event.group_size}
                                                    onChange={this.handleTextFieldChange}
                                                />

                                                <a
                                                    href="#"
                                                    style={{ textDecoration: "none", fontSize: 11, marginTop: 10, display: "inline-block" }}
                                                    onClick={(e) => this.handleShow(e)}
                                                >
                                                    Edit groups
                                                </a>
                                            </Grid>}
                                        </React.Fragment>}
                                    </Grid>
                                </Paper>

                                <Paper elevation={0} style={{ marginTop: 32 }}>
                                    <CardHeader title="Triggers" className="small-header"/>

                                    <Grid container spacing={3} style={{ padding: "0 16px 16px 16px" }}>
                                        <Grid item sm={12}>
                                            <FormControlLabel
                                                className="control-label-full"
                                                label="Has trigger"
                                                labelPlacement="start"
                                                control={
                                                    <Switch
                                                        name="has_trigger"
                                                        checked={this.state.event.has_trigger}
                                                        onChange={this.handleTextFieldChange}
                                                        color="primary"
                                                    />
                                                }
                                            />

                                            <p style={{ color: "#838383", fontSize: 14, lineHeight: "20px" }}>
                                                If enabled, then event will be available when certain action has been made.
                                            </p>
                                        </Grid>

                                        {this.state.event.has_trigger && <React.Fragment>
                                            <Grid item sm={12}>
                                                <TextField
                                                    label="Trigger name"
                                                    name="trigger"
                                                    variant="outlined"
                                                    size="small"
                                                    style={{ width: "100%" }}
                                                    value={this.state.event.trigger}
                                                    onChange={this.handleTextFieldChange}
                                                />
                                            </Grid>

                                            <Grid item sm={12}>
                                                <TextField
                                                    label="Trigger value"
                                                    name="trigger_value"
                                                    variant="outlined"
                                                    size="small"
                                                    style={{ width: "100%" }}
                                                    value={this.state.event.trigger_value}
                                                    onChange={this.handleTextFieldChange}
                                                />
                                            </Grid>
                                        </React.Fragment>}

                                        <Grid item sm={12}>
                                            <FormControlLabel
                                                className="control-label-full"
                                                label="Is traceable"
                                                labelPlacement="start"
                                                control={
                                                    <Switch
                                                        name="is_traceable"
                                                        checked={this.state.event.is_traceable}
                                                        onChange={this.handleTextFieldChange}
                                                        color="primary"
                                                    />
                                                }
                                            />

                                            <p style={{ color: "#838383", fontSize: 14, lineHeight: "20px" }}>
                                                If enabled, then event will receive data updates from other modules.
                                                This should be checked if event must track user actions like stolen resources etc.
                                            </p>
                                        </Grid>
                                    </Grid>
                                </Paper>

                                <Paper elevation={0} style={{ marginTop: 32 }}>
                                    <CardHeader title="Registration" className="small-header"/>

                                    <Grid container spacing={3} style={{ padding: "0 16px 16px 16px" }}>
                                        <Grid item sm={12}>
                                            <FormControlLabel
                                                className="control-label-full"
                                                label="Enabled"
                                                labelPlacement="start"
                                                control={
                                                    <Switch
                                                        name="registration_enabled"
                                                        checked={this.state.event.registration_enabled}
                                                        onChange={this.handleTextFieldChange}
                                                        color="primary"
                                                    />
                                                }
                                            />

                                            <p style={{ color: "#838383", fontSize: 14, lineHeight: "20px" }}>
                                                Event will be visible x days before start.
                                            </p>
                                        </Grid>

                                        {this.state.event.registration_enabled && <React.Fragment>
                                            <Grid item sm={12}>
                                                <TextField
                                                    label="Registration days"
                                                    name="registration"
                                                    variant="outlined"
                                                    size="small"
                                                    style={{ width: "100%" }}
                                                    value={this.state.event.registration}
                                                    onChange={this.handleTextFieldChange}
                                                />
                                            </Grid>
                                        </React.Fragment>}
                                    </Grid>
                                </Paper>

                                <Paper elevation={0} style={{ marginTop: 32 }}>
                                    <CardHeader title="Restricted countries" className="small-header"/>

                                    <Grid container spacing={3} style={{ padding: "0 16px 16px 16px", marginTop: 5 }}>
                                        {this.state.event.restricted_countries.map((country, index) =>
                                            <React.Fragment>
                                                <Grid item sm={9} style={{ marginTop: 10 }}>
                                                    <Autocomplete
                                                        onChange={(_, newValue) => this.handleRestrictedCountryChange(index, newValue)}
                                                        value={CountryCodes.find(item => item.code === country)}
                                                        options={CountryCodes.sort((a, b) => -b.name.localeCompare(a.name))}
                                                        getOptionLabel={option => option?.name  || ""}
                                                        renderOption={(props, item) => (
                                                            <Box component="li" {...props}>
                                                                <ReactCountryFlag countryCode={item.code} svg={true}/>
                                                                <span style={{ marginLeft: 7, fontSize: 15 }}>{item.name}</span>
                                                            </Box>
                                                        )}
                                                        renderInput={(params) => <TextField {...params} label="Country" variant="outlined" size="small"/>}
                                                    />
                                                </Grid>
                                                <Grid item sm={2} style={{ marginTop: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <Button
                                                        disableElevation
                                                        onClick={() => this.removeRestrictedCountry(index)}
                                                        color="secondary"
                                                        size="small"
                                                        variant="contained"
                                                    >
                                                        Remove
                                                    </Button>
                                                </Grid>
                                            </React.Fragment>
                                        )}

                                        <Grid item sm={12} style={{ paddingTop: 0 }}>
                                            <p style={{ color: "#838383", fontSize: 14, lineHeight: "20px" }}>
                                                Event will be disabled for users that comes from listed countries
                                            </p>

                                            <Button disableElevation variant="contained" size="small" onClick={() => this.addCountry()}>Add country</Button>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                        </Grid>
                    </TabPanel>

                    <TabPanel value="stats" style={{ paddingLeft: 0, paddingRight: 0 }}>
                        <EventStats {...this.props}/>
                    </TabPanel>

                    <Dialog
                        open={this.state.dialogVisible}
                        disableEscapeKeyDown
                        fullWidth={true}
                        maxWidth="md"
                        aria-labelledby="confirmation-dialog-title"
                        scroll="paper"
                    >
                        <DialogTitle id="confirmation-dialog-title">
                            Leaderboard HQ groups
                        </DialogTitle>
                        <DialogContent>
                            <Grid container spacing={3} style={{ margin: "1px 0" }}>
                                {(this.state?.event?.groups || []).map((group, index) => <React.Fragment>
                                    <Grid item sm={5}>
                                        <TextField
                                            label="HQ start"
                                            variant="outlined"
                                            size="small"
                                            style={{ width: "100%" }}
                                            value={group.min}
                                            onChange={(e) => this.handleGroupValueChange(e.target.value, "min", index)}
                                        />
                                    </Grid>
                                    <Grid item sm={5}>
                                        <TextField
                                            label="HQ end"
                                            variant="outlined"
                                            size="small"
                                            style={{ width: "100%" }}
                                            value={group.max}
                                            onChange={(e) => this.handleGroupValueChange(e.target.value, "max", index)}
                                        />
                                    </Grid>
                                    <Grid item sm={2}>
                                        <Button
                                            disableElevation
                                            onClick={() => this.removeGroup(index)}
                                            color="secondary"
                                            size="small"
                                            variant="contained"
                                        >
                                            Remove
                                        </Button>
                                    </Grid>
                                </React.Fragment>)}

                                <Grid item sm={12} style={{ marginTop: 15 }}>
                                    <Button
                                        disableElevation
                                        onClick={() => this.addGroup()}
                                        color="primary"
                                        size="small"
                                        variant="contained"
                                    >
                                        Add group
                                    </Button>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                disableElevation
                                onClick={() => this.handleCancel()}
                                color="primary"
                                size="small"
                                variant="contained"
                            >
                                Ok
                            </Button>
                        </DialogActions>
                    </Dialog>
                </div>
            </TabContext>
        );
    }
}

const styles: any = theme => ({
    root: {
        width: "100%",
        marginTop: 24,
        overflow: "hidden",
    },
});

export const GameEvent = createForm()(withStyles(styles)(SingleGameEventComponent));
